import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

const ThankYouEmail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(120); // 2 minutes in seconds
  const countdownIntervalRef = useRef(null);

  // Get sessionId from params or location state
  const effectiveSessionId = sessionId || location.state?.sessionId;

  useEffect(() => {
    // Load font and inject keyframes
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Poppins:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&display=swap';
    document.head.appendChild(fontLink);
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes floatBlob {
        0% { transform: translateY(0) scale(1); }
        50% { transform: translateY(-20px) scale(1.08); }
        100% { transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Start countdown timer on mount
  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Auto-redirect when countdown reaches 0
  useEffect(() => {
    if (countdown === 0) {
      navigate('/redeem');
    }
  }, [countdown, navigate]);

  // Format countdown mm:ss
  const formatCountdown = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Email',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#ccb4d6'
      });
      return;
    }

    if (!effectiveSessionId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Session ID not found',
        confirmButtonColor: '#ccb4d6'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.laviopaperie.com/api/photo-session/update-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          indukname: effectiveSessionId,
          email: email
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update email');
      }

      setLoading(false);
      setSubmitted(true);
      
      Swal.fire({
        icon: 'success',
        title: 'Email Submitted!',
        text: 'Thank you! Your email has been saved. We will send your photos and editing options soon.',
        confirmButtonColor: '#ccb4d6',
        confirmButtonText: 'OK',
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: true
      }).then((result) => {
        // Navigate to redeem page after Swal closes (either by timer or OK click)
        navigate('/redeem');
      });

    } catch (error) {
      setLoading(false);
      console.error('Error submitting email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to submit email. Please try again.',
        confirmButtonColor: '#ccb4d6'
      });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbf8ff 0%, #f4fff9 50%, #fffaf5 100%)',
      backgroundSize: '200% 200%',
      animation: 'gradientShift 16s ease infinite',
      position: 'relative',
      overflow: 'hidden',
      padding: '20px'
    }}>
      {/* Animated blobs */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-100px', left: '-80px', width: '400px', height: '400px', background: 'radial-gradient(circle at 30% 30%, rgba(255,182,193,0.6), rgba(255,182,193,0.1), transparent)', filter: 'blur(25px)', animation: 'floatBlob 15s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '-120px', right: '-90px', width: '450px', height: '450px', background: 'radial-gradient(circle at 70% 70%, rgba(173,216,230,0.6), rgba(173,216,230,0.1), transparent)', filter: 'blur(28px)', animation: 'floatBlob 18s ease-in-out infinite' }} />
      </div>

      {/* Main Card */}
      <div style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: '48px 40px',
        maxWidth: '600px',
        width: '100%',
        zIndex: 1,
        position: 'relative',
        boxShadow: '0 10px 40px rgba(204, 180, 214, 0.3)',
        border: '1px solid rgba(204, 180, 214, 0.3)'
      }}>
        {/* Countdown Timer */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: 64,
          height: 64,
          minWidth: 64,
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          <svg width="64" height="64">
            <circle
              cx="32"
              cy="32"
              r="28"
              stroke="#ccb4d6"
              strokeWidth="6"
              fill="#f8f4ff"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              stroke="#a4b5ff"
              strokeWidth="6"
              fill="none"
              strokeDasharray={2 * Math.PI * 28}
              strokeDashoffset={2 * Math.PI * 28 * (1 - countdown / 120)}
              style={{
                transition: 'stroke-dashoffset 0.7s cubic-bezier(.4,2,.6,1)',
                strokeLinecap: 'round'
              }}
            />
          </svg>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Poppins, Arial, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            color: countdown <= 15 ? '#ff6b81' : '#764ba2',
            letterSpacing: '1px',
            userSelect: 'none',
            pointerEvents: 'none',
            textShadow: '0 1px 4px #fff8',
            transition: 'color 0.3s',
            lineHeight: 1
          }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 800 }}>{formatCountdown(countdown)}</span>
          </div>
        </div>

        {/* Title */}
        <div style={{
          fontFamily: `'Great Vibes', cursive`,
          fontSize: '48px',
          lineHeight: 1.1,
          letterSpacing: '1px',
          color: '#8f72a6',
          textShadow: '0 1px 0 rgba(255,255,255,0.6)',
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          {Array.from('Thank You!').map((ch, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                animation: 'titleWave 2.2s ease-in-out infinite',
                animationDelay: `${i * 0.06}s`,
                willChange: 'transform'
              }}
            >
                {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </div>

        {/* Message */}
        <p style={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '18px',
          color: '#6c757d',
          textAlign: 'center',
          marginBottom: '32px',
          lineHeight: 1.6
        }}>
          Silahkan isi email dibawah ini untuk send photo dan melakukan editing foto di depan
        </p>

        {/* Email Form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#7c5fa6',
                marginBottom: '12px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  fontSize: '16px',
                  borderRadius: '12px',
                  border: '2px solid #ccb4d6',
                  outline: 'none',
                  fontFamily: "'Poppins', sans-serif",
                  background: '#f8f4ff',
                  color: '#7c5fa6',
                  transition: 'all 0.3s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#a28ad6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(162, 138, 214, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ccb4d6';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 0',
                borderRadius: '999px',
                border: 'none',
                background: loading 
                  ? 'linear-gradient(90deg, #ccb4d6 0%, #b4e7f8 100%)' 
                  : 'linear-gradient(90deg, #a28ad6 0%, #f8e1f4 100%)',
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                letterSpacing: '1px',
                boxShadow: '0 4px 16px 0 rgba(204,180,214,0.3)',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                fontFamily: "'Poppins', sans-serif"
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 6px 20px 0 rgba(204,180,214,0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 16px 0 rgba(204,180,214,0.3)';
              }}
            >
              {loading ? 'Submitting...' : 'Submit Email'}
            </button>
          </form>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            fontFamily: "'Poppins', sans-serif",
            color: '#7c5fa6',
            fontSize: '16px'
          }}>
            Email submitted successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default ThankYouEmail;

